import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { nanoid } from 'nanoid';
import {
	Sizes,
	generateAnimations,
	destroyAnimations,
	generateTriggers,
} from '../utils';

gsap.registerPlugin( ScrollTrigger );

export class Scroll {
	constructor( selector, options = {} ) {
		// General
		this.elements = gsap.utils.toArray( selector );
		this.sizes = new Sizes();
		this.options = {
			...options,
			devices: options?.devices || [ 'Desktop', 'Tablet', 'Mobile' ],
			start: options?.start || 'top bottom',
			end: options?.end || 'bottom top',
		};
		this.isEnable = false;

		// Animations
		this.timelines = [];
		this.textInstances = [];

		// Triggers Data
		this.viewInTrigger = {};
		this.viewOutTrigger = {};
		this.viewInBackTrigger = {};
		this.viewOutBackTrigger = {};
		this.viewInFn = [];
		this.viewOutFn = [];
		this.viewInBackFn = [];
		this.viewOutBackFn = [];
		this.triggerTimer = [];

		this.triggerIDs = [];
	}

	init() {
		if (
			! this.elements?.length ||
			this.isEnable ||
			! this.options.devices.includes( this.sizes.device )
		)
			return;
		const viewInSettings = this.options.viewIn;
		const viewOutSettings = this.options.viewOut;
		const viewInBackSettings = this.options.backViewIn;
		const viewOutBackSettings = this.options.backViewOut;

		this.elements.forEach( ( ele, index ) => {
			const [ theTimelines, theTextInstance ] = generateAnimations(
				ele,
				this.options.animations
			);
			this.timelines[ index ] = theTimelines;
			this.textInstances[ index ] = theTextInstance;

			const triggerVars = {};

			this.viewInFn[ index ] = this.#generateVars(
				viewInSettings,
				triggerVars,
				'onEnter',
				theTimelines,
				this.viewInTrigger,
				this.triggerTimer,
				index
			);
			this.viewOutFn[ index ] = this.#generateVars(
				viewOutSettings,
				triggerVars,
				'onLeave',
				theTimelines,
				this.viewOutFn,
				this.triggerTimer,
				index
			);
			this.viewInBackFn[ index ] = this.#generateVars(
				viewInBackSettings,
				triggerVars,
				'onEnterBack',
				theTimelines,
				this.viewInBackTrigger,
				this.triggerTimer,
				index
			);
			this.viewOutBackFn[ index ] = this.#generateVars(
				viewOutBackSettings,
				triggerVars,
				'onLeaveBack',
				theTimelines,
				this.viewOutBackTrigger,
				this.triggerTimer,
				index
			);

			this.triggerIDs[ index ] = `trigger-${ nanoid( 9 ) }`;
			ScrollTrigger.create( {
				trigger: ele,
				id: this.triggerIDs[ index ],
				start: this.options.start,
				end: this.options.end,
				markers: this.options.markers,
				...triggerVars,
			} );
		} );
		this.isEnable = true;
	}

	kill() {
		if ( ! this.isEnable ) return;
		this.elements.forEach( ( ele, index ) => {
			this.viewInFn[ index ]?.kill();
			this.viewOutFn[ index ]?.kill();
			this.viewInBackFn[ index ]?.kill();
			this.viewOutBackFn[ index ]?.kill();
		} );
		destroyAnimations(
			this.elements,
			this.options.animations,
			this.timelines,
			this.textInstances
		);
		this.triggerIDs.forEach( ( item ) => {
			ScrollTrigger.getById( item )?.kill( true );
		} );
		this.timelines = [];
		this.textInstances = [];
		this.viewInTrigger = {};
		this.viewOutTrigger = {};
		this.viewInBackTrigger = {};
		this.viewOutBackTrigger = {};
		this.viewInFn = [];
		this.viewOutFn = [];
		this.viewInBackFn = [];
		this.viewOutBackFn = [];
		this.triggerTimer = [];
		this.triggerIDs = [];
		this.isEnable = false;
	}

	resize() {
		this.kill();
		this.init();
	}

	destroy() {
		this.kill();
		this.elements = null;
		this.sizes = null;
		this.options = null;
		this.isEnable = null;
		this.timelines = null;
		this.textInstances = null;
		this.viewInTrigger = null;
		this.viewOutTrigger = null;
		this.viewInBackTrigger = null;
		this.viewOutBackTrigger = null;
		this.viewInFn = null;
		this.viewOutFn = null;
		this.viewInBackFn = null;
		this.viewOutBackFn = null;
		this.triggerTimer = null;
		this.triggerIDs = null;
	}

	#generateVars( setting, vars, event, theTimelines, trigger, timer, index ) {
		if ( ! setting ) return;
		const func = generateTriggers(
			setting,
			theTimelines,
			trigger,
			timer,
			index
		);
		vars[ event ] = () => {
			func.run();
		};
		return func;
	}
}
